/**
 * Draw inspection reports — photos and the requested-lines evidence table.
 *
 * `budgets-draws-report-photos.md`: the same `report_photo_reviews` call that
 * carries feasibility reports carries site inspections, told apart by
 * `reportKind`. Several inspection FILES for one draw (revisions) come back as
 * separate reviews sharing a `drawNumber` — they group under the draw, and which
 * one governs is decided by the servicing build, not here, so this lists them
 * rather than picking.
 *
 * Inspections add two things a feasibility doesn't have:
 *   • `lineEvidence[]` — the lines the borrower asked to be paid on this draw,
 *     what the report says each is complete to, and whether a photo evidences it
 *   • `requestedLinesWithoutPhotoEvidence[]` — the call-out. Soft-cost lines
 *     (plans, permits, fees) are already excluded by the backend, so everything
 *     listed here is something a photo COULD have shown and didn't.
 *
 * The two completion figures follow the doc's wording rule: the inspector's
 * printed figure is labelled as theirs, the AI's photo estimate is labelled as
 * an AI cross-check with the mandated tooltip, and the AI figure is never shown
 * on its own or used in any arithmetic.
 */
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import PhotoCameraOutlined from "@mui/icons-material/PhotoCameraOutlined";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import { Box, Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import { useMemo } from "react";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import { T, cardSx, money } from "./budgetDrawsTheme";
import ReportPhotoStrip from "./ReportPhotoStrip";
import { getPropertyOptions } from "./newDrawFields";
import { groupReviews, PHOTO_STAGE_LABEL } from "./reportPhotosModel";

const EvidenceTable = ({ rows, onOpenLine }) => {
  if (!rows?.length) return null;
  return (
    <Box sx={{ mt: 2 }}>
      <Txt
        component="div"
        sx={{ fontSize: 12.5, fontWeight: 700, color: T.ink, mb: 1 }}
      >
        Requested lines
      </Txt>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns:
            "minmax(0, 2.4fr) 110px 110px 110px minmax(0, 1.2fr)",
          gap: 1.25,
          pb: 0.75,
        }}
      >
        {["Line", "Requested", "Eligible", "% complete", "Evidence"].map((h) => (
          <Txt key={h} sx={{ fontSize: 11.5, fontWeight: 600, color: T.muted }}>
            {h}
          </Txt>
        ))}
      </Box>
      {rows.map((r, i) => {
        const id = r?.lineItemId ? String(r.lineItemId) : null;
        return (
          <Box
            key={`${r.line}-${i}`}
            sx={{
              display: "grid",
              gridTemplateColumns:
                "minmax(0, 2.4fr) 110px 110px 110px minmax(0, 1.2fr)",
              gap: 1.25,
              alignItems: "center",
              py: 0.875,
              borderTop: `1px solid ${T.line2}`,
            }}
          >
            <Box
              component={id && onOpenLine ? "button" : "span"}
              type={id && onOpenLine ? "button" : undefined}
              onClick={id && onOpenLine ? () => onOpenLine(id) : undefined}
              sx={{
                border: 0,
                background: "none",
                p: 0,
                textAlign: "left",
                fontFamily: "inherit",
                fontSize: 12.5,
                color: id && onOpenLine ? T.accentInk : T.ink2,
                cursor: id && onOpenLine ? "pointer" : "default",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                "&:hover":
                  id && onOpenLine ? { textDecoration: "underline" } : undefined,
              }}
            >
              {r.line}
            </Box>
            <Txt sx={{ fontSize: 12.5, color: T.ink, fontFamily: T.mono }}>
              {money(r.requested)}
            </Txt>
            <Txt sx={{ fontSize: 12.5, color: T.ink, fontFamily: T.mono }}>
              {money(r.eligible)}
            </Txt>
            <Txt sx={{ fontSize: 12.5, color: T.ink, fontFamily: T.mono }}>
              {r.percentCompletePerReport != null
                ? `${Math.round(r.percentCompletePerReport)}%`
                : "—"}
            </Txt>
            <Flx ac g={0.625} sx={{ minWidth: 0 }}>
              {r.evidenced ? (
                <>
                  <PhotoCameraOutlined sx={{ fontSize: 14, color: T.green }} />
                  <Txt sx={{ fontSize: 12, color: T.green, fontWeight: 600 }}>
                    {r.photoIndexes?.length || 0} photo
                    {(r.photoIndexes?.length || 0) === 1 ? "" : "s"}
                  </Txt>
                </>
              ) : r.photographable === false ? (
                <Txt sx={{ fontSize: 12, color: T.faint }}>Soft cost</Txt>
              ) : (
                <Txt sx={{ fontSize: 12, color: T.amberInk, fontWeight: 600 }}>
                  No photo
                </Txt>
              )}
              {r.stagesSeen?.length > 0 && (
                <Txt sx={{ fontSize: 11.5, color: T.faint }}>
                  ·{" "}
                  {r.stagesSeen
                    .map((s) => PHOTO_STAGE_LABEL[s] || s)
                    .join(", ")}
                </Txt>
              )}
            </Flx>
          </Box>
        );
      })}
    </Box>
  );
};

/** One inspection file. */
const InspectionReport = ({ api, dealId, review, onOpenLine, onPreviewReport }) => {
  const missing = review?.requestedLinesWithoutPhotoEvidence || [];
  const stated = review?.statedPercentComplete;
  const ai = review?.summary?.visiblePercentCompleteEstimate;

  return (
    <Box sx={{ px: 2.5, py: 2, borderTop: `1px solid ${T.line}` }}>
      <Flx ac jb g={2} sx={{ mb: 1.5, flexWrap: "wrap" }}>
        <Flx ac g={1} sx={{ minWidth: 0, flexWrap: "wrap" }}>
          <Txt sx={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>
            {review?.reportVendor || "Inspection report"}
          </Txt>
          {review?.reportDate && (
            <Txt sx={{ fontSize: 12.5, color: T.muted }}>
              {formatDate(review.reportDate, "")}
            </Txt>
          )}
        </Flx>
        {/* Doc wording rule: the inspector's figure is labelled as theirs, and
            the AI estimate never appears without it. */}
        {stated != null && (
          <Flx ac g={1.5} sx={{ flexWrap: "wrap" }}>
            <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>
              {`Inspector: ${Math.round(stated)}% complete`}
            </Txt>
            {ai != null && (
              <Tooltip title="An AI reading of the photos in this report only. A cross-check, not a measurement, and never a basis for funding.">
                <Flx ac g={0.5} sx={{ cursor: "help" }}>
                  <Txt sx={{ fontSize: 12.5, color: T.muted }}>
                    {`AI photo estimate: ${Math.round(ai)}%`}
                  </Txt>
                  <InfoOutlined sx={{ fontSize: 13, color: T.faint }} />
                </Flx>
              </Tooltip>
            )}
          </Flx>
        )}
      </Flx>

      {review?.note && (
        <Txt
          component="div"
          sx={{ fontSize: 12.5, color: T.muted, mb: 1.5, lineHeight: 1.55 }}
        >
          {review.note}
        </Txt>
      )}

      <ReportPhotoStrip
        api={api}
        dealId={dealId}
        review={review}
        onOpenLine={onOpenLine}
        onPreviewReport={onPreviewReport}
      />

      {review?.summary?.workInPlace && (
        <Txt
          component="div"
          sx={{ fontSize: 12.5, color: T.ink2, mt: 1.5, lineHeight: 1.55 }}
        >
          {review.summary.workInPlace}
        </Txt>
      )}

      {review?.summary?.concerns?.length > 0 && (
        <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.25 }}>
          {review.summary.concerns.map((c, i) => (
            <Box
              component="li"
              key={i}
              sx={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.55, mb: 0.25 }}
            >
              {c}
            </Box>
          ))}
        </Box>
      )}

      {/* The call-out. Everything listed could have been photographed and
          wasn't — soft costs are filtered out by the backend. */}
      {missing.length > 0 && (
        <Flx
          g={1}
          sx={{
            mt: 2,
            px: 1.5,
            py: 1.25,
            borderRadius: T.radiusSm,
            background: T.amberTint,
            border: `1px solid ${T.amberLine}`,
          }}
        >
          <WarningAmberRounded
            sx={{ fontSize: 17, color: T.amberInk, flexShrink: 0, mt: "1px" }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Txt
              component="div"
              sx={{ fontSize: 13, fontWeight: 700, color: T.amberInk }}
            >
              Requested with no photo evidence
            </Txt>
            <Flx ac g={0.625} sx={{ mt: 0.875, flexWrap: "wrap" }}>
              {missing.map((name) => {
                // Match the call-out name back to its lineEvidence row so the
                // chip can navigate by lineItemId rather than by name.
                const ev = (review?.lineEvidence || []).find(
                  (e) => e?.line === name,
                );
                const id = ev?.lineItemId ? String(ev.lineItemId) : null;
                const clickable = Boolean(id && onOpenLine);
                return (
                  <Box
                    key={name}
                    component={clickable ? "button" : "span"}
                    type={clickable ? "button" : undefined}
                    onClick={clickable ? () => onOpenLine(id) : undefined}
                    sx={{
                      px: 1,
                      py: "3px",
                      borderRadius: "6px",
                      border: `1px solid ${T.amberLine}`,
                      background: "#fff",
                      color: T.amberInk,
                      fontFamily: "inherit",
                      fontSize: 11.5,
                      fontWeight: 600,
                      cursor: clickable ? "pointer" : "default",
                    }}
                  >
                    {name}
                  </Box>
                );
              })}
            </Flx>
          </Box>
        </Flx>
      )}

      <EvidenceTable rows={review?.lineEvidence} onOpenLine={onOpenLine} />
    </Box>
  );
};

/** One draw's inspection files under a grey draw bar. */
const DrawGroup = ({ group, api, dealId, onOpenLine, onPreviewReport }) => (
  <Box>
    <Flx
      ac
      g={1}
      sx={{
        px: 2.5,
        py: 1,
        background: T.subtle,
        borderTop: `1px solid ${T.line}`,
      }}
    >
      <Txt sx={{ fontSize: 12.5, fontWeight: 700, color: T.ink2 }}>
        {group.drawNumber != null
          ? `Draw ${group.drawNumber}`
          : "Unnumbered inspection"}
      </Txt>
      {/* Revisions of one draw's inspection arrive as separate reviews. */}
      {group.reviews.length > 1 && (
        <Txt sx={{ fontSize: 11.5, color: T.muted }}>
          {group.reviews.length} files
        </Txt>
      )}
    </Flx>
    {group.reviews.map((review, i) => (
      <InspectionReport
        key={review?._id || review?.sourceBlobName || i}
        api={api}
        dealId={dealId}
        review={review}
        onOpenLine={onOpenLine}
        onPreviewReport={onPreviewReport}
      />
    ))}
  </Box>
);

const drawGroupKey = (group) =>
  group.drawNumber ??
  `x-${group.reviews[0]?._id || group.reviews[0]?.sourceBlobName}`;

const InspectionPhotosSection = ({
  api,
  dealId,
  reviews,
  properties,
  onOpenLine,
  onPreviewReport,
}) => {
  const propertyOptions = useMemo(
    () => getPropertyOptions(properties),
    [properties],
  );
  const multiProperty = propertyOptions.length > 1;

  // Report-photos doc: "On a multi-property loan show the report under that
  // property. `null` means unresolved: show it under 'Unassigned'" — the same
  // rule the feasibility reports follow. Within a property, files still group
  // under their draw. Single-property loans keep the flat draw list.
  const propertyGroups = useMemo(() => {
    if (!multiProperty) return null;
    const byProperty = new Map();
    (reviews || [])
      .filter((r) => r?.reportKind !== "feasibility")
      .forEach((r) => {
        const pid = r?.subjectPropertyId ? String(r.subjectPropertyId) : null;
        if (!byProperty.has(pid)) byProperty.set(pid, []);
        byProperty.get(pid).push(r);
      });
    const label = (pid) =>
      pid
        ? propertyOptions.find((o) => o.value === pid)?.label ||
          "Unknown property"
        : "Unassigned";
    // Loan property order first, then unknown ids, Unassigned last.
    const order = (pid) => {
      if (pid == null) return Number.MAX_SAFE_INTEGER;
      const i = propertyOptions.findIndex((o) => o.value === pid);
      return i === -1 ? Number.MAX_SAFE_INTEGER - 1 : i;
    };
    return [...byProperty.entries()]
      .sort(([x], [y]) => order(x) - order(y))
      .map(([pid, rs]) => ({
        propertyId: pid,
        label: label(pid),
        draws: groupReviews(rs).inspectionDraws,
      }));
  }, [multiProperty, reviews, propertyOptions]);

  const { inspectionDraws } = useMemo(() => groupReviews(reviews), [reviews]);
  if (!inspectionDraws.length) return null;

  const renderDraw = (group) => (
    <DrawGroup
      key={drawGroupKey(group)}
      group={group}
      api={api}
      dealId={dealId}
      onOpenLine={onOpenLine}
      onPreviewReport={onPreviewReport}
    />
  );

  return (
    <Box sx={cardSx}>
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        <Txt sx={{ fontSize: 16, fontWeight: 700, color: T.ink }}>
          Inspection reports
        </Txt>
        <Txt component="div" sx={{ fontSize: 12.5, color: T.muted, mt: 0.25 }}>
          Site photos and the lines each draw was paid on.
        </Txt>
      </Box>

      {propertyGroups
        ? propertyGroups.map((pg) => (
            <Box key={pg.propertyId || "unassigned"}>
              <Flx
                ac
                g={1}
                sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${T.line}` }}
              >
                <Txt sx={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>
                  {pg.label}
                </Txt>
                <Txt sx={{ fontSize: 11.5, color: T.muted }}>
                  {pg.draws.length} {pg.draws.length === 1 ? "draw" : "draws"}
                </Txt>
              </Flx>
              {pg.draws.map(renderDraw)}
            </Box>
          ))
        : inspectionDraws.map(renderDraw)}
    </Box>
  );
};

InspectionPhotosSection.propTypes = {
  api: PropTypes.object.isRequired,
  dealId: PropTypes.string,
  reviews: PropTypes.array,
  properties: PropTypes.array,
  onOpenLine: PropTypes.func,
  onPreviewReport: PropTypes.func,
};

export default InspectionPhotosSection;
